import { takeEvery, put, fork, call, all, select } from 'redux-saga/effects';
import { paymentReducerActions, paymentReducerCallTypes } from "..";
import { verifyHCAPTCHAApi, generatePDFReceipt, createBSPPayPaymentApi, processBSPPayOrderApi, createBSPCardPaymentApi, processBSPCardOrderApi } from "./paymentAPI";
import { receiptReducerActions } from "../../ReceiptRetrieval";
import * as uis from "../../UI/redux/uiSlice";

import { encryptPayload } from '../../../utils/encryptPayload';
import logman from '../../../../utilities/logman';


// ****************************************************** REFERENCE ********************************************************

export const actionTypes = {
    CreateBSPPayPayment: "[CreateBSPPayPayment] Create BSPPay Payment API Action",
    ProcessBSPPayOrder: "[ProcessBSPPayOrder] Process BSPPay Order API Action",
    CreateBSPCardPayment: "[CreateBSPCardPayment] Create BSPCard Payment API Action",
    ProcessBSPCardOrder: "[ProcessBSPCardOrder] Process BSPCard Order API Action",
    BSPPayPayment: "[BSPPayPayment] BSP Pay Payment API Action",
    VerifyHCAPTCHA: "[VerifyHCAPTCHA] Verify HCAPTCHA Api Action",
    PDFReceipt: "[PDF Receipt] Generate PDF Receipt Api Action",
    Reset: "[Reset State] Reset State Action"
};

export const actions = {

    createBSPPayPayment: (params) => ({ type: actionTypes.CreateBSPPayPayment, payload: { params } }),
    processBSPPayOrder: (customerPayment) => ({ type: actionTypes.ProcessBSPPayOrder, payload: { customerPayment } }),
    createBSPCardPayment: (params) => ({ type: actionTypes.CreateBSPCardPayment, payload: { params } }),
    processBSPCardOrder: (customerPayment) => ({ type: actionTypes.ProcessBSPCardOrder, payload: { customerPayment } }),
    verifyHCAPTCHA: (params) => ({ type: actionTypes.VerifyHCAPTCHA, payload: { params } }),
    pdfReceipt: (params) => ({ type: actionTypes.PDFReceipt, payload: { params } }),
    reset: () => ({ type: actionTypes.Reset, payload: { undefined } })

};

// ****************************************************** WATCHERS ********************************************************


//BSPPAY

export function* onCreateBSPPayPaymentStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.payment }));

        const getAuth = (state) => state.auth;
        const auth = yield select(getAuth);

        let encryptedObject = encryptPayload(payload.params);
        
        let reqBody = {
            username: auth.userName,
            sessionKey: auth.sessionKey,
            guid: encryptedObject.guid,
            key: encryptedObject.key,
            encryption: encryptedObject.encryption
        };
        
        const response = yield call(createBSPPayPaymentApi, reqBody);

        logman.debug("PaymentSaga::onCreateBSPPayPaymentStartAsync::response => " + JSON.stringify(response));        

        if(response.status === 200){
            let receiptCreated = response.data;
            let reportStr = response.data.base64ReportDoc;
            let receiptNum = response.data.receiptNumber;
            yield put(receiptReducerActions.receiptCreated({ receiptCreated, reportStr, receiptNum }));
            yield put(paymentReducerActions.paymentCreated({ receiptNum }));
            yield put(paymentReducerActions.generatedPDFReport({ reportStr }));
        }
    }
    catch(error){     

        if(error.response.status === 500){

            const { comments } = error.response.data;

            yield put(paymentReducerActions.paymentFailed({ comments }));
            
        } else {

            if(error.response.status === 403){

                let status = error.response.status;
                let msg = "Forbidden. You do not have access to view this page.";

                yield put(uis.actions.error({ status, msg }));
                
            } else if(error.response.status === 404){

                let status = error.response.status;
                let msg = "Page Not Found.";

                yield put(uis.actions.error({ status, msg }));
            } else if(error.response.status === 500){

                let status = error.response.status;
                let msg = "Internal Error";

                yield put(uis.actions.error({ status, msg }));
            }

        }

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.payment,
            error: "There was an error processing the payment."
        }));
    }

};

export function* onProcessBSPPayOrderStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.order }));

        let encryptedObject = encryptPayload(payload.customerPayment);
        
        let reqBody = {
            username: "guest",
            password: "guest",
            guid: encryptedObject.guid,
            key: encryptedObject.key,
            encryption: encryptedObject.encryption
        };
        
        const response = yield call(processBSPPayOrderApi, reqBody);

        if(response.status === 200){
            
            const { callback_url, customer_id, encryptedPayload, merchant_key, order_id, payment_rail_type_id } = response.data;
            
            let orderDetails = {
                callbackUrl: callback_url,
                customerId: customer_id,
                encryptedPayload: encryptedPayload,
                merchantKey: merchant_key,
                orderId: order_id,
                paymentRailTypeId: payment_rail_type_id
            };

            yield put(paymentReducerActions.orderProcessed({ orderDetails }));
        }
    }
    catch(error){       

        if(error.response.status === 403){

            let status = error.response.status;
            let msg = "Forbidden. You do not have access to view this page.";

            yield put(uis.actions.error({ status, msg }));
            
        } else if(error.response.status === 404){

            let status = error.response.status;
            let msg = "Page Not Found.";

            yield put(uis.actions.error({ status, msg }));
        } else if(error.response.status === 500){

            let status = error.response.status;
            let msg = "Internal Error";

            yield put(uis.actions.error({ status, msg }));
        }

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.order,
            error: "There was an error processing the payment order details."
        }));

    }

};

//BSPCARD

export function* onCreateBSPCardPaymentStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.payment }));

        const getAuth = (state) => state.auth;
        const auth = yield select(getAuth);

        let encryptedObject = encryptPayload(payload.params);
        
        let reqBody = {
            username: auth.userName,
            sessionKey: auth.sessionKey,
            guid: encryptedObject.guid,
            key: encryptedObject.key,
            encryption: encryptedObject.encryption
        };
        
        const response = yield call(createBSPCardPaymentApi, reqBody);

        logman.debug("PaymentSaga::onCreateBSPCardPaymentStartAsync::response => " + JSON.stringify(response));        

        if(response.status === 200){
            let receiptCreated = response.data;
            let reportStr = response.data.base64ReportDoc;
            let receiptNum = response.data.receiptNumber;
            yield put(receiptReducerActions.receiptCreated({ receiptCreated, reportStr, receiptNum }));
            yield put(paymentReducerActions.paymentCreated({ receiptNum }));
            yield put(paymentReducerActions.generatedPDFReport({ reportStr }));
        }
    }
    catch(error){     

        if(error.response.status === 500){

            const { comments } = error.response.data;

            yield put(paymentReducerActions.paymentFailed({ comments }));
            
        } else {

            if(error.response.status === 403){

                let status = error.response.status;
                let msg = "Forbidden. You do not have access to view this page.";

                yield put(uis.actions.error({ status, msg }));
                
            } else if(error.response.status === 404){

                let status = error.response.status;
                let msg = "Page Not Found.";

                yield put(uis.actions.error({ status, msg }));
            } else if(error.response.status === 500){

                let status = error.response.status;
                let msg = "Internal Error";

                yield put(uis.actions.error({ status, msg }));
            }

        }

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.payment,
            error: "There was an error processing the payment."
        }));
    }

};

export function* onProcessBSPCardOrderStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.order }));

        let encryptedObject = encryptPayload(payload.customerPayment);
        
        let reqBody = {
            username: "guest",
            password: "guest",
            guid: encryptedObject.guid,
            key: encryptedObject.key,
            encryption: encryptedObject.encryption
        };
        
        const response = yield call(processBSPCardOrderApi, reqBody);

        if(response.status === 200){
            
            const {
                customer_id,
                id,
                payment_rail_type_id,
                currency_type_id,
                ...rest
              } = response.data;
              
              const orderDetails = {
                ...rest,
                customerId: customer_id,
                orderId: id,
                currencyTypeId: currency_type_id,
                paymentRailTypeId: payment_rail_type_id
              };

            yield put(paymentReducerActions.orderProcessed({ orderDetails }));
        }
    }
    catch(error){       

        if(error.response.status === 403){

            let status = error.response.status;
            let msg = "Forbidden. You do not have access to view this page.";

            yield put(uis.actions.error({ status, msg }));
            
        } else if(error.response.status === 404){

            let status = error.response.status;
            let msg = "Page Not Found.";

            yield put(uis.actions.error({ status, msg }));
        } else if(error.response.status === 500){

            let status = error.response.status;
            let msg = "Internal Error";

            yield put(uis.actions.error({ status, msg }));
        }

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.order,
            error: "There was an error processing the payment order details."
        }));

    }

};

//****************************************************COMMON WATCHERS******************************************************/

export function* onVerifyHCAPTCHAStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.captcha }));

        const response = yield call(verifyHCAPTCHAApi, payload.params);        

        if(response.status === 200){
            let result = response.data.success;
            yield put(paymentReducerActions.captchaValidated({ result }));
        }
    }
    catch(error){

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.captcha,
            error: "There was an error verifying the CAPTCHA."
        }));

    }

};

export function* onResetStartAsync(){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.action }));


        yield put(paymentReducerActions.resetState());
    }
    catch(error){

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.action,
            error: "There was an error when reloading the payment form."
        }));

    }

};

export function* onGeneratePDFReceiptStartAsync({payload}){

    try{
        
        yield put(paymentReducerActions.startCall({ callType: paymentReducerCallTypes.action }));

        // const getAuth = (state) => state.auth;
        // const auth = yield select(getAuth);
        
        // let reqBody = {
        //     username: auth.userName,
        //     sessionKey: auth.sessionKey,
        //     input: payload.params
        // };

        // const response = yield call(generatePDFReceipt, reqBody);   

        const response = yield call(generatePDFReceipt, payload.params);        

        if(response.status === 200){
            let dataStr = response.data;
            yield put(paymentReducerActions.generatedPDFReport({ dataStr }));
        }
    }
    catch(error){

        if(error.response.status === 403){

            let status = error.response.status;
            let msg = "Forbidden. You do not have access to view this page.";

            yield put(uis.actions.error({ status, msg }));
            
        } else if(error.response.status === 404){

            let status = error.response.status;
            let msg = "Page Not Found.";

            yield put(uis.actions.error({ status, msg }));

        } else if(error.response.status === 500){

            let status = error.response.status;
            let msg = "Internal Error";

            yield put(uis.actions.error({ status, msg }));
        }

        // yield put(uis.actions.showNotification({
        //     status:"error",
        //     title:"Error",
        //     message: "There was an error generating the PDF e-receipt."
        // }));

        yield put(paymentReducerActions.catchError({
            callType: paymentReducerCallTypes.action,
            error: "There was an error generating the PDF e-receipt."
        }));

    }

};

// ****************************************************** WORKERS ********************************************************
export function* createBSPPayPayment(){

    yield takeEvery(actionTypes.CreateBSPPayPayment, onCreateBSPPayPaymentStartAsync);    
};

export function* processBSPPayOrder(){

    yield takeEvery(actionTypes.ProcessBSPPayOrder, onProcessBSPPayOrderStartAsync);    
};

export function* createBSPCardPayment(){

    yield takeEvery(actionTypes.CreateBSPCardPayment, onCreateBSPCardPaymentStartAsync);    
};

export function* processBSPCardOrder(){

    yield takeEvery(actionTypes.ProcessBSPCardOrder, onProcessBSPCardOrderStartAsync);    
};


export function* verifyHCAPTCHA(){

    yield takeEvery(actionTypes.VerifyHCAPTCHA, onVerifyHCAPTCHAStartAsync);
};

export function* reset(){
    
    yield takeEvery(actionTypes.Reset, onResetStartAsync);
};

export function* pdfReceipt(){

    yield takeEvery(actionTypes.PDFReceipt, onGeneratePDFReceiptStartAsync);
};

const paymentSagas = [
    fork(createBSPPayPayment),
    fork(processBSPPayOrder),
    fork(createBSPCardPayment),
    fork(processBSPCardOrder),
    fork(verifyHCAPTCHA),
    fork(pdfReceipt),
    fork(reset)
];

export function* saga(){
    yield all ([...paymentSagas]);
};